/** GLSL ES 3.00 sources (ARCHITECTURE §4). All textures are y-up. */

export const VS_FULLSCREEN = `#version 300 es
out vec2 v_uv;
void main() {
  vec2 p = vec2(float((gl_VertexID << 1) & 2), float(gl_VertexID & 2));
  v_uv = p;
  gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
}
`;

/** Canvas RGBA8 (straight alpha, sRGB) → linear premultiplied, times an HDR scale (1 for diffuse layers). */
export const FS_LINEARIZE = `#version 300 es
precision highp float;
precision highp sampler2D;
uniform sampler2D u_src;
uniform float u_scale;
in vec2 v_uv;
out vec4 o;
vec3 toLinear(vec3 c) {
  return mix(c / 12.92, pow((c + 0.055) / 1.055, vec3(2.4)), step(0.04045, c));
}
void main() {
  vec4 s = texture(u_src, v_uv);
  o = vec4(toLinear(s.rgb) * s.a * u_scale, s.a);
}
`;

/** HDR light sprites: additive ellipses with a 1 px smooth edge (ARCHITECTURE §4.3). */
export const FS_LIGHTS = `#version 300 es
precision highp float;
const int MAX_LIGHTS = 32;
uniform int u_count;
uniform vec4 u_lights[MAX_LIGHTS];
uniform vec3 u_colors[MAX_LIGHTS];
out vec4 o;
void main() {
  vec2 p = gl_FragCoord.xy;
  vec3 acc = vec3(0.0);
  for (int i = 0; i < MAX_LIGHTS; i++) {
    if (i >= u_count) break;
    vec4 l = u_lights[i];
    float r = length((p - l.xy) / l.zw);
    float edge = 1.0 / min(l.z, l.w);
    acc += u_colors[i] * (1.0 - smoothstep(1.0 - edge, 1.0, r));
  }
  o = vec4(acc, 0.0);
}
`;

/**
 * Scaled bilinear copy: downsample (one fetch at the 2×2 corner), upsample-add, composite.
 * u_zoom > 1 magnifies around the centre (aphakic spectacles, D-021).
 */
export const FS_BLIT = `#version 300 es
precision highp float;
precision highp sampler2D;
uniform sampler2D u_src;
uniform float u_scale;
uniform float u_zoom;
in vec2 v_uv;
out vec4 o;
void main() {
  o = texture(u_src, 0.5 + (v_uv - 0.5) / u_zoom) * u_scale;
}
`;

/** Gather convolution with a per-channel kernel (alpha uses the G kernel, stored in K.a). */
export const FS_CONVOLVE = `#version 300 es
precision highp float;
precision highp int;
precision highp sampler2D;
uniform sampler2D u_src;
uniform sampler2D u_kernel;
uniform int u_R;
uniform ivec2 u_size;
out vec4 o;
void main() {
  ivec2 p = ivec2(gl_FragCoord.xy);
  int n = 2 * u_R + 1;
  vec4 acc = vec4(0.0);
  for (int j = 0; j < n; j++) {
    for (int i = 0; i < n; i++) {
      vec4 k = texelFetch(u_kernel, ivec2(i, j), 0);
      if (k.r + k.g + k.b <= 0.0) continue;
      ivec2 q = clamp(p + ivec2(u_R - i, u_R - j), ivec2(0), u_size - 1);
      acc += k * texelFetch(u_src, q, 0);
    }
  }
  o = acc;
}
`;

/** Separable Gaussian, one direction per pass. */
export const FS_GAUSS = `#version 300 es
precision highp float;
precision highp sampler2D;
uniform sampler2D u_src;
uniform vec2 u_step;
uniform float u_sigma;
uniform int u_radius;
in vec2 v_uv;
out vec4 o;
void main() {
  vec4 acc = vec4(0.0);
  float wsum = 0.0;
  for (int i = -32; i <= 32; i++) {
    if (i < -u_radius || i > u_radius) continue;
    float w = exp(-float(i * i) / (2.0 * u_sigma * u_sigma));
    acc += texture(u_src, v_uv + u_step * float(i)) * w;
    wsum += w;
  }
  o = acc / wsum;
}
`;

/** Scatter mix and transmission (OPTICS §8): out = T ⊙ ((1 − f)·img + f·S). */
export const FS_SCATTER = `#version 300 es
precision highp float;
precision highp sampler2D;
uniform sampler2D u_img;
uniform sampler2D u_scatter;
uniform float u_f;
uniform vec3 u_trans;
in vec2 v_uv;
out vec4 o;
void main() {
  vec4 img = texture(u_img, v_uv);
  vec4 s = texture(u_scatter, v_uv);
  o = vec4(u_trans * ((1.0 - u_f) * img.rgb + u_f * s.rgb), img.a);
}
`;

/**
 * Starburst streaks around bright lights (D-018), added on top of the pane. Each spike has a radial profile
 * (1 − t)²·c/(a + c) and a Gaussian cross-section, both normalised, so a light's streaks carry
 * u_energy × its flux in total.
 */
export const FS_STREAKS = `#version 300 es
precision highp float;
const int MAX_SL = 24;
const int MAX_SPIKES = 16;
uniform int u_count;
uniform vec4 u_geo[MAX_SL];
uniform vec3 u_col[MAX_SL];
uniform vec4 u_spikes[MAX_SPIKES];
uniform int u_spikeCount;
uniform float u_energy;
uniform float u_width;
uniform vec3 u_trans;
out vec4 o;
void main() {
  vec2 p = gl_FragCoord.xy;
  vec3 acc = vec3(0.0);
  float gNorm = 1.0 / (2.5066 * u_width);
  for (int i = 0; i < MAX_SL; i++) {
    if (i >= u_count) break;
    vec4 g = u_geo[i];
    vec2 d = p - g.xy;
    if (dot(d, d) > g.w * g.w) continue;
    float sum = 0.0;
    for (int k = 0; k < MAX_SPIKES; k++) {
      if (k >= u_spikeCount) break;
      vec4 s = u_spikes[k];
      float along = dot(d, s.xy);
      float len = g.w * s.z;
      if (along <= 0.0 || along >= len) continue;
      float perp = d.x * s.y - d.y * s.x;
      float t = along / len;
      float c = 0.05 * len;
      float radial = (1.0 - t) * (1.0 - t) * c / (along + c) / (0.0903 * len);
      sum += s.w * radial * exp(-perp * perp / (2.0 * u_width * u_width)) * gNorm;
    }
    acc += u_col[i] * g.z * sum;
  }
  o = vec4(acc * u_energy * u_trans, 0.0);
}
`;

/**
 * Retinal pass (ARCHITECTURE §4.9, OPTICS §7). Damage d ∈ [0, 1] around fixation → blur that grows
 * with d, fade toward the heavily blurred local mean, desaturation, slight dimming. Never black;
 * DR floaters are the one dark, semi-transparent exception. Also: amblyopia (a correction-independent
 * neural blur, D-020) and dark adaptation / night blindness (D-023).
 */
export const FS_RETINA = `#version 300 es
precision highp float;
precision highp sampler2D;
uniform sampler2D u_img;
uniform sampler2D u_b0;
uniform sampler2D u_b1;
uniform sampler2D u_b2;
uniform sampler2D u_b3;
uniform sampler2D u_bAmb;
uniform vec2 u_fix;
uniform vec2 u_size;
uniform float u_ppd;
uniform ivec4 u_stages;
uniform int u_hr;
uniform int u_amblyopia;
uniform float u_ambContrast;
uniform float u_dark;
uniform vec4 u_spots[20];
uniform int u_spotCount;
uniform vec4 u_floaters[4];
uniform int u_floaterCount;
in vec2 v_uv;
out vec4 o;

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}
float vnoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), f.x), f.y);
}
float band(float e, float e0, float e1, float soft) {
  return smoothstep(e0 - soft, e0 + soft, e) * (1.0 - smoothstep(e1 - soft, e1 + soft, e));
}

void main() {
  vec2 p = gl_FragCoord.xy;
  vec2 d = (p - u_fix) / u_ppd;
  float ecc = length(d);
  float ang = degrees(atan(d.y, d.x));
  float n = vnoise(vec2(ang * 0.08, ecc * 0.35) + 3.7);
  float eccN = ecc * (0.88 + 0.24 * n);

  float dmg = 0.0;
  float haze = 0.0;
  vec2 warpDeg = vec2(0.0);

  // Glaucoma: arcuate scotomata, then tunnel, then a central island.
  if (u_stages.x == 1) {
    float sup = smoothstep(-15.0, 25.0, ang) * (1.0 - smoothstep(155.0, 195.0, ang));
    float nasal = mix(0.6, 1.0, clamp(0.5 - 0.5 * d.x / max(ecc, 0.5), 0.0, 1.0));
    dmg = max(dmg, 0.55 * band(eccN, 10.0, 20.0, 1.5) * sup * nasal);
  } else if (u_stages.x == 2) {
    float sup = smoothstep(-15.0, 25.0, ang) * (1.0 - smoothstep(155.0, 195.0, ang));
    float inf = smoothstep(-175.0, -135.0, ang) * (1.0 - smoothstep(-45.0, -5.0, ang));
    dmg = max(dmg, band(eccN, 8.0, 25.0, 2.0) * max(0.85 * sup, 0.5 * inf));
    dmg = max(dmg, 0.6 * smoothstep(23.0, 28.0, eccN));
  } else if (u_stages.x == 3) {
    dmg = max(dmg, 0.95 * smoothstep(11.0, 16.0, eccN));
  } else if (u_stages.x == 4) {
    dmg = max(dmg, smoothstep(3.0, 6.0, eccN));
  }

  // AMD: central metamorphopsia (warp), blur, and at the late stage dimming with a brownish tint.
  float amdCore = 0.0;
  if (u_stages.y > 0) {
    float amdR = u_stages.y == 1 ? 1.2 : (u_stages.y == 2 ? 2.2 : 4.5);
    float dist = u_stages.y == 1 ? 0.15 : (u_stages.y == 2 ? 0.30 : 0.25);
    float strength = u_stages.y == 1 ? 0.3 : (u_stages.y == 2 ? 0.65 : 0.9);
    float zone = 1.0 - smoothstep(amdR * 1.2, amdR * 2.0, ecc);
    vec2 warp = vec2(vnoise(d * 0.9 + 11.0), vnoise(d * 0.9 + 47.0)) - 0.5;
    warpDeg += warp * 2.0 * dist * zone;
    amdCore = 1.0 - smoothstep(amdR * 0.55, amdR * 1.15, eccN);
    dmg = max(dmg, strength * amdCore);
  }

  // Focal spots: diabetic microhaemorrhages, hypertensive cotton-wool spots and flame haemorrhages.
  for (int i = 0; i < 20; i++) {
    if (i >= u_spotCount) break;
    vec4 s = u_spots[i];
    float r = length(d - s.xy) / s.z * (0.9 + 0.2 * vnoise(d * 2.0 + float(i) * 5.3));
    dmg = max(dmg, s.w * (1.0 - smoothstep(0.6, 1.1, r)));
  }

  // Diabetic retinopathy: central oedema, vitreous haze.
  if (u_stages.z >= 2) {
    float oedema = (u_stages.z == 2 ? 0.5 : 0.65) * (1.0 - smoothstep(2.0, 3.5, eccN));
    dmg = max(dmg, oedema);
  }
  if (u_stages.z == 3) haze = 0.15;

  // Hypertensive retinopathy IV: disc and macular oedema with a macular star (distortion).
  if (u_hr >= 4) {
    dmg = max(dmg, 0.55 * (1.0 - smoothstep(1.8, 3.2, eccN)));
    float zone = 1.0 - smoothstep(2.0, 3.0, ecc);
    vec2 warp = vec2(vnoise(d * 1.6 + 71.0), vnoise(d * 1.6 + 13.0)) - 0.5;
    warpDeg += warp * 0.24 * zone;
    haze = max(haze, 0.05);
  }

  // Retinitis pigmentosa: mid-peripheral ring scotoma closing into a tunnel, then a central island.
  if (u_stages.w == 1) {
    dmg = max(dmg, 0.5 * band(eccN, 25.0, 40.0, 3.0));
  } else if (u_stages.w == 2) {
    dmg = max(dmg, max(0.8 * band(eccN, 15.0, 30.0, 3.0), 0.9 * smoothstep(28.0, 34.0, eccN)));
  } else if (u_stages.w == 3) {
    dmg = max(dmg, 0.95 * smoothstep(8.0, 12.0, eccN));
  } else if (u_stages.w == 4) {
    dmg = max(dmg, smoothstep(3.0, 5.0, eccN));
  }

  vec2 uv = v_uv + warpDeg * u_ppd / u_size;
  float dd = clamp(dmg, 0.0, 1.0);
  vec3 img = texture(u_img, uv).rgb;
  vec3 b0 = texture(u_b0, uv).rgb;
  vec3 b1 = texture(u_b1, uv).rgb;
  vec3 b2 = texture(u_b2, uv).rgb;
  vec3 b3 = texture(u_b3, uv).rgb;
  vec3 blurred = dd < 0.33 ? mix(img, b0, dd / 0.33)
               : (dd < 0.66 ? mix(b0, b1, (dd - 0.33) / 0.33) : mix(b1, b2, (dd - 0.66) / 0.34));
  vec3 c = mix(blurred, b3, clamp(0.65 * dd + haze, 0.0, 1.0));
  float luma = dot(c, vec3(0.2126, 0.7152, 0.0722));
  c = mix(c, vec3(luma), 0.6 * dd);
  c *= 1.0 - 0.2 * dd;
  if (u_stages.y == 3) {
    c = mix(c, luma * 0.55 * vec3(1.0, 0.84, 0.66), amdCore * 0.85);
  }

  // Amblyopia: a neural blur matched to the degree's acuity (sigma ~ 1.4 MAR), outside damaged areas.
  if (u_amblyopia > 0) {
    vec3 ab = texture(u_bAmb, uv).rgb;
    c = mix(c, ab, 1.0 - dd);
    c = mix(c, b3, u_ambContrast * (1.0 - dd));
  }

  // Dark adaptation and night blindness: darker, faint detail lost, colour fades (rod vision).
  if (u_dark < 0.999) {
    float loss = 1.0 - u_dark;
    c = mix(c, b1, 0.7 * loss) * u_dark;
    float l2 = dot(c, vec3(0.2126, 0.7152, 0.0722));
    c = mix(c, vec3(l2), 0.8 * loss);
  }

  // Floaters: dark, semi-transparent blobs that cast shadows.
  float shade = 1.0;
  for (int i = 0; i < 4; i++) {
    if (i >= u_floaterCount) break;
    vec4 f = u_floaters[i];
    vec2 q = (d - f.xy) / f.zw;
    float r = length(q) * (0.85 + 0.3 * vnoise(d * 1.7 + float(i) * 9.1));
    shade *= 1.0 - 0.55 * (1.0 - smoothstep(0.6, 1.05, r));
  }
  c *= shade;
  o = vec4(c, texture(u_img, uv).a);
}
`;

/**
 * Split, exposure, tone map, sRGB encode → default framebuffer.
 * Day: linear to 0.8, soft knee to 1 (OPTICS §8). Night (u_knee > 0, D-016): linear display up to the knee,
 * then luminance is compressed logarithmically up to u_white, keeping the hue; very bright coloured lights
 * burn out towards white instead of clipping.
 */
export const FS_FINAL = `#version 300 es
precision highp float;
precision highp sampler2D;
uniform sampler2D u_left;
uniform sampler2D u_right;
uniform float u_split;
uniform float u_exposure;
uniform float u_knee;
uniform float u_k;
uniform float u_white;
in vec2 v_uv;
out vec4 o;
vec3 tonemap(vec3 x) {
  vec3 knee = 0.8 + 0.2 * (1.0 - exp(-(x - 0.8) / 0.2));
  return mix(x, knee, step(0.8, x));
}
float srgb1(float c) {
  return c <= 0.0031308 ? c * 12.92 : 1.055 * pow(c, 1.0 / 2.4) - 0.055;
}
float linear1(float c) {
  return c <= 0.04045 ? c / 12.92 : pow((c + 0.055) / 1.055, 2.4);
}
vec3 toSrgb(vec3 c) {
  return mix(c * 12.92, 1.055 * pow(c, vec3(1.0 / 2.4)) - 0.055, step(0.0031308, c));
}
vec3 toneNight(vec3 x) {
  float L = dot(x, vec3(0.2126, 0.7152, 0.0722));
  if (L <= u_knee) return x;
  float d0 = srgb1(u_knee);
  float d = d0 + (1.0 - d0) * log(1.0 + (L - u_knee) / u_k) / log(1.0 + (u_white - u_knee) / u_k);
  vec3 c = x * (linear1(min(d, 1.0)) / L);
  // Highlights lose colour as they approach white (photoreceptor saturation). This also keeps the
  // three-channel chromatic spread around white point lights from showing as a purple ring.
  float sat = 1.0 - 0.8 * smoothstep(d0, 1.0, d);
  c = mix(vec3(dot(c, vec3(0.2126, 0.7152, 0.0722))), c, sat);
  float m = max(max(c.r, c.g), c.b);
  if (m > 1.0) c = mix(c / m, vec3(1.0), clamp((m - 1.0) / m, 0.0, 1.0));
  return c;
}
void main() {
  vec4 s = v_uv.x < u_split ? texture(u_left, v_uv) : texture(u_right, v_uv);
  vec3 x = max(s.rgb, 0.0) * u_exposure;
  vec3 c = u_knee > 0.0 ? toneNight(x) : tonemap(x);
  o = vec4(toSrgb(clamp(c, 0.0, 1.0)), 1.0);
}
`;
